"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { createClient } from "@/lib/supabase/client";
import { Mail, Lock, Eye, EyeOff, AlertCircle } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const supabase = createClient();
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setError("Onjuiste inloggegevens. Probeer het opnieuw.");
      setLoading(false);
      return;
    }

    router.push("/dashboard");
    router.refresh();
  };

  return (
    <>
      {/* Mobile logo with animation */}
      <div className="mb-8 flex flex-col items-center gap-3 lg:hidden">
        <div className="relative">
          <div className="bg-brand-500/20 absolute inset-0 rounded-xl blur-xl" aria-hidden="true" />
          <video
            autoPlay
            loop
            muted
            playsInline
            poster="/logo-square.png"
            className="relative h-20 w-20 rounded-xl"
          >
            <source src="/logo-animation.mp4" type="video/mp4" />
          </video>
        </div>
        <span className="text-lg font-bold text-white">
          Closer
          <span className="from-brand-300 to-brand-400 bg-gradient-to-r bg-clip-text text-transparent">
            AI
          </span>
        </span>
      </div>

      <h2 className="text-3xl font-bold tracking-[-0.03em] text-white">Welkom terug</h2>
      <p className="text-text-secondary mt-1.5">
        Log in om verder te <span className="text-brand-300">trainen</span>
      </p>

      <form onSubmit={handleLogin} className="mt-8 space-y-5">
        {error && (
          <div className="flex items-center gap-2.5 rounded-xl border border-red-500/20 bg-red-500/[0.06] px-4 py-3 text-sm text-red-400 backdrop-blur-sm">
            <AlertCircle size={16} className="shrink-0" />
            {error}
          </div>
        )}

        <div>
          <label className="text-text-secondary mb-1.5 block text-sm font-medium">
            E-mailadres
          </label>
          <div className="group relative">
            <Mail
              size={18}
              className="text-text-tertiary group-focus-within:text-brand-400 absolute top-1/2 left-3.5 -translate-y-1/2 transition-[color,transform] duration-200 group-focus-within:scale-110"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="naam@bedrijf.nl"
              required
              className="focus:border-brand-400 focus:ring-brand-500/20 w-full rounded-xl border border-white/[0.08] bg-white/[0.04] py-3 pr-4 pl-11 text-sm text-white placeholder-white/30 transition-[background-color,border-color,box-shadow] duration-200 focus:bg-white/[0.06] focus:shadow-[0_0_20px_rgba(37,99,235,0.15)] focus:ring-2 focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="text-text-secondary mb-1.5 block text-sm font-medium">Wachtwoord</label>
          <div className="group relative">
            <Lock
              size={18}
              className="text-text-tertiary group-focus-within:text-brand-400 absolute top-1/2 left-3.5 -translate-y-1/2 transition-[color,transform] duration-200 group-focus-within:scale-110"
            />
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="focus:border-brand-400 focus:ring-brand-500/20 w-full rounded-xl border border-white/[0.08] bg-white/[0.04] py-3 pr-12 pl-11 text-sm text-white placeholder-white/30 transition-[background-color,border-color,box-shadow] duration-200 focus:bg-white/[0.06] focus:shadow-[0_0_20px_rgba(37,99,235,0.15)] focus:ring-2 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Verberg wachtwoord" : "Toon wachtwoord"}
              className="text-text-tertiary hover:text-brand-300 focus-visible:text-brand-400 absolute top-1/2 right-3.5 -translate-y-1/2 transition-[color,transform] duration-150 active:scale-95"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <Button type="submit" fullWidth size="lg" disabled={loading}>
          {loading ? "Bezig met inloggen..." : "Inloggen"}
        </Button>
      </form>
    </>
  );
}
